import type { Page } from "../api.types";
import type { IOrganizationMinimal } from "../organizations";

export interface ITenantOrganizationGroupMinimal {
    id: number;
    name: string;
}

export interface IOrganizationGroupMinimal extends ITenantOrganizationGroupMinimal {
    owner_organization_id: number;
}

export interface IOrganizationGroupListItem extends IOrganizationGroupMinimal {
    owner_organization: IOrganizationMinimal;
    member_count: number;
    created_at: string;
}

export interface IOrganizationGroupSkillMinimal {
    id: number;
    name: string;
}

export interface IOrganizationGroupChecklistMinimal {
    id: number;
    name: string;
}

export interface IOrganizationGroupDetail extends IOrganizationGroupListItem {
    members: IOrganizationMinimal[];
    assigned_skills: IOrganizationGroupSkillMinimal[];
    assigned_checklists: IOrganizationGroupChecklistMinimal[];
    excluded_skills?: IOrganizationGroupSkillMinimal[];
}

export const confirmedExcludedSkills = (
    group: Pick<IOrganizationGroupDetail, "excluded_skills"> | null | undefined
): IOrganizationGroupSkillMinimal[] | null =>
    group && Array.isArray(group.excluded_skills)
        ? group.excluded_skills
        : null;

export interface IOrganizationGroupCreate {
    name: string;
    owner_organization_id: number;
}

export interface IOrganizationGroupUpdate {
    name: string;
}

export interface IOrganizationGroupMemberCreate {
    organization_id: number;
}

export interface IOrganizationGroupSkillExclusionCreate {
    skill_ids: number[];
}

export interface IOrganizationGroupListParams {
    page: number;
    size: number;
    search: string;
}

export type IOrganizationGroupPage = Page<IOrganizationGroupListItem>;

export type OrganizationGroupErrorDetail =
    | { code: "organization_group_name_conflict" }
    | { code: "organization_group_name_invalid" }
    | {
          code: "organization_group_assignment_conflict";
          group: { id: number; name: string };
      }
    | {
          code: "organization_group_not_empty";
          member_count: number;
          assigned_skill_count: number;
          assigned_checklist_count: number;
      }
    | { code: "organization_group_shared_root_conflict" }
    | { code: "organization_group_reference_conflict" }
    | {
          code: "organization_unavailable";
          organization_id: number;
      }
    | { code: "skill_not_excludable"; skill_ids: number[] };

const messageForOrganizationGroupError = (
    detail: OrganizationGroupErrorDetail
) => {
    switch (detail.code) {
        case "organization_group_name_conflict":
            return "An organization group with that name already exists.";
        case "organization_group_name_invalid":
            return "Group names must be 1-120 visible characters and cannot contain commas.";
        case "organization_group_assignment_conflict":
            return `This organization is already assigned to ${detail.group.name}.`;
        case "organization_group_not_empty":
            return "Remove all members, assigned skills and assigned checklists before deleting this group.";
        case "organization_group_shared_root_conflict":
            return "This organization already has a Shared folder assigned to another group.";
        case "organization_group_reference_conflict":
            return "This group changed while it was being deleted. Refresh and try again.";
        case "organization_unavailable":
            return "This organization is no longer available.";
        case "skill_not_excludable":
            return "One or more selected skills can no longer be excluded. Refresh and try again.";
    }
};

export class OrganizationGroupError extends Error {
    readonly code: OrganizationGroupErrorDetail["code"];
    readonly detail: OrganizationGroupErrorDetail;
    readonly status: number | undefined;

    constructor(detail: OrganizationGroupErrorDetail, status?: number) {
        super(messageForOrganizationGroupError(detail));
        this.name = "OrganizationGroupError";
        this.code = detail.code;
        this.detail = detail;
        this.status = status;
    }
}

export class OrganizationGroupRemovalUnconfirmedError extends Error {
    constructor() {
        super(
            "The organization was removed, but the group could not be refreshed. Reload to verify the current members."
        );
        this.name = "OrganizationGroupRemovalUnconfirmedError";
    }
}

export class SkillExclusionRemovalUnconfirmedError extends Error {
    readonly cause: unknown;

    constructor(cause?: unknown) {
        super("Exclusion removal unconfirmed");
        this.name = "SkillExclusionRemovalUnconfirmedError";
        this.cause = cause;
    }
}

export const organizationGroupErrorMessage = (
    error: unknown,
    fallback: string
) => (error instanceof OrganizationGroupError ? error.message : fallback);
